import React, { useEffect, useState } from "react";
import api from "../../services/api";

const CHANNELS = [
  ["inApp", "In the app"],
  ["push", "Mobile push"],
  ["email", "Email"],
  ["whatsapp", "WhatsApp"],
];

const AUTOMATIONS = [
  ["welcome", "Welcome", "Sent when someone creates an account."],
  ["advert", "Every 30 days", "Don't burn your waste, sell to Jhonks now."],
  ["inactivity", "14 days quiet", "Sent when someone has not opened Jhonks for 14 days."],
  ["newMonth", "New month", "Welcome to the new month on the 1st."],
  ["priceUpdates", "Price changes", "Sent to everyone when a material price changes."],
  ["bounty", "Bounty ready", "Sent when someone qualifies for a reward."],
  ["yearlyEvents", "Yearly days", "New Year, Earth Day, Independence Day, Christmas, and the other saved days."],
];

function AnnouncementsAdmin() {
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [engagement, setEngagement] = useState({});
  const [templateName, setTemplateName] = useState("");
  const [draft, setDraft] = useState({ title: "", message: "", audience: "all", channels: CHANNELS.map(([id]) => id) });
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const response = await api.get("/admin/announcements");
    setTemplates(response.data.data.templates || []);
    setCampaigns(response.data.data.campaigns || []);
    setEngagement(response.data.data.engagement || {});
    setTemplateName(response.data.data.whatsappBroadcastTemplate || "");
  };

  useEffect(() => {
    load().catch(() => setError("Could not load announcements."));
  }, []);

  const saveAutomation = async (key, value) => {
    const next = { ...engagement, [key]: value };
    setEngagement(next);
    await api.put("/admin/announcements/settings", { engagement: { [key]: value } });
    setMessage("Automatic messages updated.");
  };

  const saveTemplateName = async () => {
    await api.put("/admin/announcements/settings", { whatsappBroadcastTemplate: templateName });
    setMessage("WhatsApp template name saved.");
  };

  const saveTemplate = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.put(`/admin/announcements/templates/${editing._id}`, {
        title: editing.title,
        message: editing.message,
        enabled: editing.enabled,
      });
      setEditing(null);
      setMessage("Saved.");
      await load();
    } catch (saveError) {
      setError(saveError.response?.data?.message || "Could not save that message.");
    } finally {
      setBusy(false);
    }
  };

  const send = async (payload) => {
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/announcements/send", payload);
      setMessage("Sending. People who turned updates off are skipped.");
      setDraft({ title: "", message: "", audience: "all", channels: CHANNELS.map(([id]) => id) });
      setTimeout(() => load().catch(() => {}), 1500);
    } catch (sendError) {
      setError(sendError.response?.data?.message || "Could not send.");
    } finally {
      setBusy(false);
    }
  };

  const toggleChannel = (id) => {
    setDraft((current) => ({
      ...current,
      channels: current.channels.includes(id)
        ? current.channels.filter((item) => item !== id)
        : [...current.channels, id],
    }));
  };

  return (
    <div className="space-y-6">
      <section className="rounded-3xl bg-gradient-to-br from-green-800 to-green-950 p-6 text-white">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-green-100">Jhonks updates</p>
        <h1 className="mt-2 text-3xl font-bold">Announcements</h1>
        <p className="mt-2 max-w-2xl text-sm text-green-50">
          Send one message now, or leave the automatic ones on. Each person gets it in the app, on their phone, by email, and on WhatsApp when those are switched on in their profile.
        </p>
      </section>

      {error && <p className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>}
      {message && <p className="rounded-2xl bg-green-50 px-4 py-3 text-sm text-green-800">{message}</p>}

      <section className="rounded-3xl bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Automatic messages</h2>
            <p className="mt-1 text-sm text-gray-500">
              These run once each morning from 8:00. Run today's checks sends that same pass now: quiet accounts, the 30-day reminder, a new-month note on the 1st or 2nd, today's yearly event, and bounty qualification. Anyone who already received that message this period is skipped.
            </p>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const response = await api.post("/admin/announcements/run");
                setMessage(response.data.message);
              } catch (runError) {
                setError(runError.response?.data?.message || "Could not run the checks.");
              } finally {
                setBusy(false);
              }
            }}
            className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            Run today's checks
          </button>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {AUTOMATIONS.map(([key, label, detail]) => (
            <label key={key} className="flex items-start justify-between gap-3 rounded-2xl border border-gray-100 px-4 py-3">
              <span>
                <span className="block font-semibold text-gray-900">{label}</span>
                <span className="mt-1 block text-sm text-gray-500">{detail}</span>
              </span>
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={engagement[key] !== false}
                onChange={(event) => saveAutomation(key, event.target.checked)}
              />
            </label>
          ))}
        </div>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <input
            value={templateName}
            onChange={(event) => setTemplateName(event.target.value)}
            placeholder="Approved WhatsApp template name, optional"
            className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm"
          />
          <button type="button" onClick={saveTemplateName} className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-800">
            Save template
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Without an approved template, WhatsApp only reaches people who messaged Jhonks in the last 24 hours. The template should have two text variables: title, then message.
        </p>
      </section>

      <section className="rounded-3xl bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900">Send once</h2>
        <p className="mt-1 text-sm text-gray-500">Use this for a technical issue, a delay, or a general note.</p>
        <div className="mt-4 space-y-3">
          <input
            value={draft.title}
            onChange={(event) => setDraft({ ...draft, title: event.target.value })}
            placeholder="Title"
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
          />
          <textarea
            value={draft.message}
            onChange={(event) => setDraft({ ...draft, message: event.target.value })}
            placeholder="Message"
            rows={4}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
          />
          <div className="flex flex-wrap gap-2">
            {[
              ["all", "Everyone"],
              ["sellers", "Sellers"],
              ["agents", "Agents"],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setDraft({ ...draft, audience: id })}
                className={`rounded-full px-3 py-1.5 text-sm font-semibold ${draft.audience === id ? "bg-green-700 text-white" : "bg-gray-100 text-gray-700"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 text-sm text-gray-700">
            {CHANNELS.map(([id, label]) => (
              <label key={id} className="flex items-center gap-2">
                <input type="checkbox" checked={draft.channels.includes(id)} onChange={() => toggleChannel(id)} />
                {label}
              </label>
            ))}
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() => send(draft)}
            className="rounded-xl bg-green-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            Send now
          </button>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-bold text-gray-900">Saved messages</h2>
        {templates.map((template) => (
          <article key={template._id} className="rounded-3xl bg-white p-5 shadow-sm">
            {editing?._id === template._id ? (
              <div className="space-y-3">
                <input
                  value={editing.title}
                  onChange={(event) => setEditing({ ...editing, title: event.target.value })}
                  className="w-full rounded-xl border px-3 py-2 text-sm"
                />
                <textarea
                  value={editing.message}
                  onChange={(event) => setEditing({ ...editing, message: event.target.value })}
                  rows={3}
                  className="w-full rounded-xl border px-3 py-2 text-sm"
                />
                <div className="flex gap-2">
                  <button type="button" onClick={saveTemplate} className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white">Save</button>
                  <button type="button" onClick={() => setEditing(null)} className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {template.kind === "event" ? `${template.day}/${template.month}` : template.kind}
                  </p>
                  <h3 className="font-semibold text-gray-900">{template.title}</h3>
                  <p className="mt-1 text-sm text-gray-600">{template.message}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => api.put(`/admin/announcements/templates/${template._id}`, { enabled: !template.enabled }).then(load)}
                    className={`rounded-full px-3 py-1.5 text-sm font-semibold ${template.enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}
                  >
                    {template.enabled ? "On" : "Off"}
                  </button>
                  <button type="button" onClick={() => setEditing(template)} className="rounded-full bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-800">
                    Edit
                  </button>
                  {!/\{\{(material|title|reward|price)\}\}/.test(template.message) && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => send({
                      title: template.title.replaceAll("{{month}}", new Date().toLocaleString("en-NG", { month: "long", timeZone: "Africa/Lagos" })),
                      message: template.message.replaceAll("{{month}}", new Date().toLocaleString("en-NG", { month: "long", timeZone: "Africa/Lagos" })),
                      audience: "all",
                      channels: CHANNELS.map(([id]) => id),
                      templateKey: template.key,
                      type: template.kind === "event" ? "event" : template.key === "price" ? "price_update" : template.key === "inactivity" ? "inactivity" : template.key === "advert" ? "advert" : template.key === "new-month" ? "monthly" : template.key === "bounty" ? "bounty_qualified" : template.key === "welcome" ? "welcome" : "announcement",
                    })}
                    className="rounded-full bg-green-700 px-3 py-1.5 text-sm font-semibold text-white"
                  >
                    Send now
                  </button>
                  )}
                </div>
              </div>
            )}
          </article>
        ))}
      </section>

      <section className="rounded-3xl bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900">Recent sends</h2>
        <div className="mt-3 divide-y">
          {campaigns.map((campaign) => (
            <div key={campaign._id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
              <div>
                <p className="font-semibold text-gray-900">{campaign.title}</p>
                <p className="text-gray-500">{campaign.message}</p>
              </div>
              <p className="text-gray-600">
                {campaign.status} · {campaign.sentCount || 0} sent · {campaign.skippedCount || 0} skipped
              </p>
            </div>
          ))}
          {!campaigns.length && <p className="py-6 text-sm text-gray-500">No announcements sent yet.</p>}
        </div>
      </section>
    </div>
  );
}

export default AnnouncementsAdmin;
